import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

// Initial state
const initialState = {
  wheels: [],
  primaryWheel: null,
  profile: null,
  page: 1,
  total_pages: 0,
  folders: [],
  selectedFolder: null,
  folder_page: 1,
  folder_total_pages: 0,
  isUserProfile: false,
};

const gallerySlice = createSlice({
  name: "gallery",
  initialState,
  reducers: {
    addWheel: (state, action) => {
      state.wheels.push(action.payload);
    },

    addWheels: (state, action) => {
      state.wheels = action.payload.wheels;
      state.page = action.payload.page;
      state.total_pages = action.payload.total_pages;
    },
    resetWheels: (state) => {
      state.wheels = [];
      state.page = 1;
      state.total_pages = 0;
    },

    deleteWheel: (state, action) => {
      state.wheels = state.wheels.filter((wheel) => wheel.id != action.payload);
    },
    updateWheel: (state, action) => {
      const { id, updates } = action.payload;
      const index = state.wheels.findIndex((wheel) => wheel.id === id);

      if (index !== -1) {
        const updatedWheel = {
          ...state.wheels[index],
          ...updates,
        };
        state.wheels.splice(index, 1);
        state.wheels.unshift(updatedWheel);
      }
    },

    updatePrimaryWheel: (state, action) => {
      const { id, updates } = action.payload;
      if (state.primaryWheel && state.primaryWheel.id === id) {
        state.primaryWheel = {
          ...state.primaryWheel,
          ...updates,
        };
      }
    },
    updateProfile: (state, action) => {
      const { updates } = action.payload;

      state.profile = {
        ...state.profile,
        ...updates,
      };
    },
    updateProfileBusinessBox: (state, action) => {
      const { updates } = action.payload;
      if (state.profile) {
        state.profile.business_box = updates;
      } else {
        console.warn("Cannot update business_box: profile is null");
      }
    },
    getWheelById: (state, action) => {
      return state.wheels.find((wheel) => wheel.id === action.payload);
    },
    getAllWheels: (state) => {
      return state.wheels;
    },
    setPrimaryWheel: (state, action) => {
      const payload = action?.payload;
      if (state?.primaryWheel?.id) {
        state.wheels.unshift({ ...state?.primaryWheel, visibility: 1 });
      }
      state.wheels = state?.wheels?.filter((wheel) => wheel?.id != payload?.id);
      state.primaryWheel = { ...payload, visibility: 1 };
    },
    setModifiedPrimaryOnFirst: (state, action) => {
      state.wheels.unshift(action.payload);
    },

    // Folders  Reducers //
    addFolder: (state, action) => {
      if (action.payload?.parentId === null) {
        state.folders.push(action.payload);
      } else {
        const parentFolder = findFolderById(
          state.folders,
          action.payload?.parentId
        );
        if (parentFolder) {
          parentFolder.items.push(action.payload);
        }
      }
    },

    addFolders: (state, action) => {
      state.folders = action.payload.folders;
      state.folder_page = action.payload.page;
      state.folder_total_pages = action.payload.total_pages;
    },

    deleteFolder: (state, action) => {
      state.folders = state.folders.filter(
        (folder) => folder.id != action.payload
      );
    },

    updateFolder: (state, action) => {
      const { id, updates } = action.payload;
      // const index = state.folders.findIndex((folder) => folder.id === id);

      // if (index !== -1) {
      //   const updatedFolder = {
      //     ...state.folders[index],
      //     ...updates,
      //   };
      //   state.folders.splice(index, 1);
      //   state.folders.unshift(updatedFolder);
      const folder = findFolderById(state.folders, id);
      if (folder) {
        Object.assign(folder, updates);
      }
    },

    updateItem: (state, action) => {
      const { itemId, updates } = action.payload;

      const updateItemInFolder = (folder) => {
        if (!folder.items || !Array.isArray(folder.items)) {
          return folder;
        }

        return {
          ...folder,
          items: folder.items.map((item) => {
            if (item.id === itemId) {
              return { ...item, ...updates };
            } else if (item.type === "folder") {
              return updateItemInFolder(item);
            }
            return item;
          }),
        };
      };

      return {
        ...state,
        folders: state.folders.map((folder) => {
          if (folder.id === itemId) {
            return { ...folder, ...updates };
          } else {
            return updateItemInFolder(folder);
          }
        }),
      };
    },

    moveItem: (state, action) => {
      const { sourceId, destinationId, itemId } = action.payload;

      const sourceFolder = findFolderById(state.folders, sourceId);
      if (!sourceFolder) return;

      const destinationFolder = findFolderById(state.folders, destinationId);
      if (!destinationFolder) return;

      const itemIndex = sourceFolder.items.findIndex(
        (item) => item.id === itemId
      );
      if (itemIndex === -1) return;

      const [itemToMove] = sourceFolder.items.splice(itemIndex, 1);
      destinationFolder.items.push(itemToMove);
    },

    moveFolder: (state, action) => {
      const { sourceId, destinationId } = action.payload;

      const sourceFolder = findFolderById(state.folders, sourceId);
      if (!sourceFolder) return;

      const destinationFolder = findFolderById(state.folders, destinationId);
      if (!destinationFolder) return;

      // Remove the source folder from its current parent
      const currentParentFolder = findFolderById(
        state.folders,
        sourceFolder.parentId
      );
      if (currentParentFolder) {
        currentParentFolder.items = currentParentFolder.items.filter(
          (item) => item.id !== sourceId
        );
      }

      destinationFolder.items.push({
        ...sourceFolder,
        parentId: destinationId,
      });
      state.folders = state.folders.filter((folder) => folder.id !== sourceId);
    },

    addItem: (state, action) => {
      let { data, parentId } = action.payload || {};
      const parentFolder = findFolderById(state.folders, parentId);
      if (parentFolder) {
        let alreadyExists = parentFolder.items.find(
          (item) => item.id === data.id
        );
        if (alreadyExists) {
          // toast.error("Wheel already exists in folder");
          return;
        }
        parentFolder.items.push(data);
      }
    },
    addItemsToFolder: (state, action) => {
      let { data, parentId } = action.payload || {};
      const parentFolder = findFolderByIdRecursive(state.folders, parentId);
      if (parentFolder) {
        data.forEach((item) => {
          let alreadyExists = parentFolder.items.find(
            (folderItem) => folderItem.id === item.id
          );
          if (!alreadyExists) {
            parentFolder.items.push(item);
          }
        });
      }
      state.folders = updateFoldersRecursive(state.folders, parentFolder);
    },

    deleteItem: (state, action) => {
      const { itemId } = action.payload;

      const findParentFolder = (folders, itemId) => {
        for (let folder of folders) {
          if (folder.items.some((item) => item.id === itemId)) {
            return folder;
          }
          if (folder.items && folder.items.length > 0) {
            const parentFolder = findParentFolder(folder.items, itemId);
            if (parentFolder) {
              return parentFolder;
            }
          }
        }
        return null;
      };

      const updateFolders = (folders, updatedFolder) => {
        return folders.map((folder) => {
          if (folder.id === updatedFolder.id) {
            return updatedFolder;
          }
          if (folder.items && folder.items.length > 0) {
            return {
              ...folder,
              items: updateFolders(folder.items, updatedFolder),
            };
          }
          return folder;
        });
      };

      const parentFolder = findParentFolder(state.folders, itemId);

      if (!parentFolder) return;

      parentFolder.items = parentFolder.items.filter(
        (item) => item.id !== itemId
      );

      state.folders = updateFolders(state.folders, parentFolder);
    },

    // gallery-collections-wheel-stat-increment: a drag-drop wheel move
    // (useFolderMove.moveWheelToFolder) optimistically relocates the wheel in
    // `items` via addItem/deleteItem above, but collectionCover.js's
    // collectionWheelCount() deliberately never derives the badge from
    // `items.length` (gordon-collection-count-includes-subcollections —
    // `items` only holds DIRECT children, so counting it undercounts a
    // collection with sub-collections). The badge instead always trusts the
    // BE-reported `content_amount`/`size`, which a client-side item move never
    // touches — so the visible count stayed stale until a hard reload even
    // though the move itself succeeded server-side (measured live on
    // web-stg 2026-09-02, bundle main.132cd7ca.js: PUT .../move/ 200s, badge
    // unchanged with no reload, correct only after reload). This bumps the
    // trusted count field itself by `delta` so the optimistic update and the
    // badge agree without ever recomputing from `items`.
    adjustFolderCount: (state, action) => {
      const { folderId, delta } = action.payload || {};
      const folder = findFolderById(state.folders, folderId);
      if (!folder) return;
      if (folder.content_amount != null) {
        folder.content_amount = Math.max(0, folder.content_amount + delta);
      }
      if (folder.size != null) {
        folder.size = Math.max(0, folder.size + delta);
      }
    },

    clonedItem: (state, action) => {
      const { itemId, parentId } = action.payload;
      const parentFolder = findFolderById(state.folders, parentId);
      if (parentFolder) {
        const itemToClone = findFolderById(state.folders, itemId);
        if (itemToClone) {
          parentFolder.items.push({ ...itemToClone });
        }
      }
    },

    setSelectedFolder: (state, action) => {
      state.selectedFolder = action.payload;
    },

    setPrimaryWheelv2: (state, action) => {
      state.primaryWheel = action.payload;
    },

    setIsUserProfile: (state, action) => {
      state.isUserProfile = action.payload;
    },

    clearSelectedFolder: (state) => {
      state.selectedFolder = null;
    },

    resetFolders: (state) => {
      state.folders = [];
      state.folder_page = 1;
      state.folder_total_pages = 0;
      state.selectedFolder = null;
    },

    // Folders  Reducers //

    clearPrimaryWheel: (state) => {
      state.primaryWheel = null;
    },
    setProfile: (state, action) => {
      state.profile = action.payload;
    },
    clearProfile: (state) => {
      state.profile = null;
    },

    resetGallery: (state) => {
      return initialState;
    },
  },
});

function findFolderById(folders, folderId) {
  for (let folder of folders) {
    if (folder.id === folderId) {
      return folder;
    }
    const foundInSubfolder = findFolderById(
      folder.items.filter((item) => item.type === "folder"),
      folderId
    );
    if (foundInSubfolder) {
      return foundInSubfolder;
    }
  }
  return null;
}

const findFolderByIdRecursive = (folders, folderId) => {
  for (const folder of folders) {
    if (folder.id === folderId) {
      return folder;
    } else if (folder.items && folder.items.length > 0) {
      const nestedFolder = findFolderByIdRecursive(folder.items, folderId);
      if (nestedFolder) {
        return nestedFolder;
      }
    }
  }
  return null;
};

const updateFoldersRecursive = (folders, parentFolder) => {
  return folders.map((folder) => {
    if (folder?.id === parentFolder?.id) {
      return parentFolder;
    } else if (folder.items && folder.items.length > 0) {
      return {
        ...folder,
        items: updateFoldersRecursive(folder.items, parentFolder),
      };
    }
    return folder;
  });
};

// Exporting actions
export const {
  addWheel,
  addWheels,
  deleteWheel,
  updateWheel,
  updatePrimaryWheel,
  getWheelById,
  getAllWheels,
  setWheels,
  setPrimaryWheel,
  setPrimaryWheelv2,
  clearPrimaryWheel,
  setProfile,
  updateProfile,
  clearProfile,
  resetWheels,
  setModifiedPrimaryOnFirst,
  addFolder,
  addFolders,
  resetFolders,
  deleteFolder,
  updateFolder,
  setSelectedFolder,
  clearSelectedFolder,
  moveFolder,
  addItem,
  updateItem,
  deleteItem,
  adjustFolderCount,
  clonedItem,
  addItemsToFolder,
  updateProfileBusinessBox,
  resetGallery,
  setIsUserProfile,
} = gallerySlice.actions;

// Selectors
export const selectAllWheels = (state) => state.gallery.wheels;
export const selectWheelById = (state, id) =>
  state.gallery.wheels.find((wheel) => wheel.id === id);
export const selectPrimaryWheel = (state) => state.gallery.primaryWheel;
export const selectProfile = (state) => state.gallery.profile;
export const selectPage = (state) => state.gallery.page;
export const selectTotalPages = (state) => state.gallery.total_pages;
export const selectFolders = (state) => state.gallery.folders;
export const selectSelectedFolder = (state) => state.gallery.selectedFolder;
export const selectFolderPage = (state) => state.gallery.folder_page;
export const selectFolderTotalPages = (state) =>
  state.gallery.folder_total_pages;
export const selectIsUserProfile = (state) => state.gallery.isUserProfile;

// Reducer
export default gallerySlice.reducer;
